<template>
  <el-dialog
    id="holderModal"
    title="Edit Coworker"
    custom-class="holderDialog"
    :visible.sync="showLocal"
    :width="dialogWidth"
  >
    <!-- Edit mode -->
    <template v-if="mode === 'edit'">
      <el-form
        v-if="editDialog.user"
        ref="holderForm"
        :model="input"
        :rules="rules"
      >
        <el-row
          :gutter="24"
        >
          <el-col
            :span="responsiveCol"
          >
            <!-- Relationship -->
            <el-form-item
              :label="$t('leader.coworker.relationship')"
              prop="org_relationship"
            >
              <el-select
                v-model="input.org_relationship"
                :value="input.org_relationship"
              >
                <el-option
                  v-for="(group, gKey) in relationshipList"
                  :key="`group_${gKey}`"
                  :label="group.text"
                  :value="group.value"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col
            :span="responsiveCol"
          >
            <!-- User Email -->
            <el-form-item
              :label="$t('common.email')"
              prop="email"
            >
              <el-input
                v-model="input.email"
                disabled
                autocomplete="off"
              />
            </el-form-item>
          </el-col>
          <el-col
            :span="responsiveCol"
          >
            <!-- User name -->
            <el-form-item
              :label="$t('common.firstName')"
              prop="first_name"
              :error="apiRules.first_name ? apiRules.first_name.join(', ') : null"
            >
              <el-input
                v-model="input.first_name"
                autocomplete="off"
                @change="apiRuleClear('first_name')"
              />
            </el-form-item>
          </el-col>
          <el-col
            :span="responsiveCol"
          >
            <el-form-item
              :label="$t('common.lastName')"
              prop="last_name"
              :error="apiRules.last_name ? apiRules.last_name.join(', ') : null"
            >
              <el-input
                v-model="input.last_name"
                autocomplete="off"
                @change="apiRuleClear('last_name')"
              />
            </el-form-item>
          </el-col>
        </el-row>

        <el-form-item v-if="['laptop', 'desktop'].includes($mq)">
          <template #label>
            <div class="actionDesc">
              <span>
                {{ $t('leader.coworker.actions') }}
                <span style="font-size: 12px; font-weight: normal;">
                  {{ $t('leader.coworker.actionDesc') }}
                </span>
              </span>
            </div>
          </template>
          <div class="actionWrap">
            <el-row
              id="mamProgress"
              v-loading="loading"
              :gutter="25"
              :class="['mobile', 'tablet'].includes($mq) ? 'mobileMode' : 'deskMode'"
            >
              <el-radio-group
                v-model="selectedAction"
                class="nextActionRadio"
              >
                <el-radio :label="'activate'">
                  {{ input.is_active ? $t('common.deactivate') : $t('common.activate') }}
                  <span class="additionalLabel">
                    <template v-if="input.is_active">
                      {{ $t('leader.coworker.deactivateResult') }}
                    </template>
                    <template v-else>
                      {{ $t('leader.coworker.activateResult') }}
                    </template>
                  </span>
                </el-radio>
                <el-tooltip
                  v-if="!isConfirmed && session <= 3"
                  class="item"
                  effect="dark"
                  :content="$t('leader.coworker.deleteTooltip')"
                  placement="top"
                >
                  <el-radio
                    :label="'delete'"
                    :disabled="isConfirmed || session > 3"
                  >
                    {{ $t('common.delete') }}
                    <span class="additionalLabel">
                      {{ $t('leader.coworker.deleteResult') }}
                    </span>
                  </el-radio>
                </el-tooltip>
              </el-radio-group>
              <el-button
                round
                size="mini"
                @click="selectedAction = null"
              >
                {{ $t('common.unselect') }}
              </el-button>
            </el-row>
          </div>
        </el-form-item>
      </el-form>

      <div slot="footer">
        <el-button
          round
          type="plain"
          style="width: 100%;"
          :style="{
            maxWidth: ['mobile', 'tablet'].includes($mq) ? null : '200px',
          }"
          @click="showLocal = false"
        >
          {{ $t('common.cancel') }}
        </el-button>

        <el-button
          round
          type="primary"
          style="width: 100%;"
          :style="{
            maxWidth: ['mobile', 'tablet'].includes($mq) ? null : '200px',
            margin: ['mobile', 'tablet'].includes($mq) ? '5px 0' : null,
          }"
          :loading="loading"
          :disabled="loading"
          @click="toggleSave"
        >
          {{ $t('common.save') }}
        </el-button>
      </div>
    </template>

    <!-- Delete mode -->
    <template v-else-if="['delete', 'reactivate'].includes(mode)">
      <div class="secondModeWrap">
        <div
          v-if="mode === 'delete'"
          class="secondDesc"
        >
          {{ $t('leader.coworker.confirmRemoveShort') }}
        </div>
        <div
          v-else-if="mode === 'reactivate'"
          class="secondDesc"
        >
          {{ $t('leader.coworker.confirmReactivateShort') }}
        </div>
        <el-row
          class="secondValueRow"
          :gutter="24"
        >
          <el-col :span="responsiveCol">
            <div class="customLabel">
              {{ $t('leader.coworker.relationship') }}
            </div>
            <div class="staticValue">
              {{ (relationshipList.find((el) => el.value === input.org_relationship)).text }}
            </div>
          </el-col>
          <el-col :span="responsiveCol">
            <div class="customLabel">
              {{ $t('common.email') }}
            </div>
            <div class="staticValue">
              {{ input.email ? input.email : '-' }}
            </div>
          </el-col>
          <el-col :span="responsiveCol">
            <div class="customLabel">
              {{ $t('common.firstName') }}
            </div>
            <div class="staticValue">
              {{ input.first_name ? input.first_name : '-' }}
            </div>
          </el-col>
          <el-col :span="responsiveCol">
            <div class="customLabel">
              {{ $t('common.lastName') }}
            </div>
            <div class="staticValue">
              {{ input.last_name ? input.last_name : '-' }}
            </div>
          </el-col>
        </el-row>
      </div>

      <div slot="footer">
        <template v-if="mode === 'delete'">
          <el-button
            round
            type="plain"
            style="width: 100%;"
            :style="{
              maxWidth: ['mobile', 'tablet'].includes($mq) ? null : '200px',
            }"
            @click="mode = 'edit'"
          >
            {{ $t('common.cancel') }}
          </el-button>

          <el-button
            round
            type="danger"
            style="width: 100%;"
            :style="{
              maxWidth: ['mobile', 'tablet'].includes($mq) ? null : '200px',
              margin: ['mobile', 'tablet'].includes($mq) ? '5px 0' : null,
            }"
            :loading="loading"
            :disabled="loading"
            @click="deleteStakeholder"
          >
            {{ $t('common.delete') }}
          </el-button>
        </template>

        <template v-else-if="mode === 'reactivate'">
          <el-button
            round
            type="plain"
            style="width: 100%;"
            :style="{
              maxWidth: ['mobile', 'tablet'].includes($mq) ? null : '200px',
            }"
            @click="showLocal = false"
          >
            {{ $t('common.cancel') }}
          </el-button>
          <el-button
            round
            type="primary"
            style="width: 100%;"
            :style="{
              maxWidth: ['mobile', 'tablet'].includes($mq) ? null : '200px',
              margin: ['mobile', 'tablet'].includes($mq) ? '5px 0' : null,
            }"
            :loading="loading"
            :disabled="loading"
            @click="toggleActivate(true)"
          >
            {{ $t('leader.coworker.reactivate') }}
          </el-button>
        </template>
      </div>
    </template>
  </el-dialog>
</template>

<script>
import { mapState } from 'vuex';
import DialogSetting from '@/mixins/dialog';
import api from '@/api';
import apiRules from '@/mixins/apiRules';

export default {
  mixins: [
    DialogSetting,
    apiRules,
  ],
  props: {
    editDialog: {
      type: Object,
      default: () => ({
        flag: false,
        user: null,
      }),
    },

    isConfirmed: {
      type: Boolean,
      default: false,
    },

    session: {
      type: Number,
      default: 0,
    },
  },

  data() {
    return {
      mode: 'edit',
      loading: false,
      selectedAction: null,
      relationshipList: [
        { text: this.$t('stakeholderGroup.manager'), value: 'manager' },
        { text: this.$t('stakeholderGroup.direct_report'), value: 'direct_report' },
        { text: this.$t('stakeholderGroup.peer'), value: 'peer' },
        { text: this.$t('stakeholderGroup.other'), value: 'other' },
      ],
      input: {
        org_relationship: '',
        first_name: '',
        last_name: '',
        email: '',
        survey_language: 'english',
        is_active: true,
      },
      rules: {
        // first_name: [{
        //   required: true,
        //   message: 'First name is required',
        //   trigger: 'blur',
        // }],
        // last_name: [{
        //   required: true,
        //   message: 'Last name is required',
        //   trigger: 'blur',
        // }],
        email: [
          { required: true, message: this.$t('common.rule.email.required'), trigger: 'blur' },
          { type: 'email', message: this.$t('common.rule.email.correct'), trigger: 'blur' },
          { validator: this.validateEmail, trigger: 'blur' },
        ],
        survey_language: [{
          required: true,
          message: 'Language is required',
          trigger: ['blur', 'change'],
        }],
      },
    };
  },

  computed: {
    ...mapState({
      user: (state) => state.user,
      role_type: (state) => state.user.user.role_type,
    }),

    capitalizedName() {
      const name = {
        first: '',
        last: '',
      };

      if (this.input.first_name) {
        name.first = this.input.first_name.charAt(0).toUpperCase() + this.input.first_name.slice(1);
      }

      if (this.input.last_name) {
        name.last = this.input.last_name.charAt(0).toUpperCase() + this.input.last_name.slice(1);
      }

      return name;
    },

    responsiveCol() {
      let col = 12;
      if (['mobile', 'tablet'].includes(this.$mq)) {
        col = 24;
      }
      return col;
    },
  },

  watch: {
    show: {
      immediate: true,
      handler(newValue) {
        if (newValue && this.editDialog.user) {
          if (!this.editDialog.user.is_active) { this.mode = 'reactivate'; }
          this.input = JSON.parse(JSON.stringify(this.editDialog.user));
        } else {
          this.mode = 'edit';
          this.selectedAction = null;
        }
      },
    },
  },

  mounted() {
    if (this.editDialog.user) {
      this.input = this.editDialog.user;
    }
  },

  methods: {
    resetForm() {
      this.$refs.holderForm.resetFields();
      this.input = {
        org_relationship: '',
        first_name: '',
        last_name: '',
        email: '',
        survey_language: 'english',
        is_active: true,
      };
    },

    closeModal() {
      this.showLocal = false;
      this.resetForm();
    },

    toggleSave() {
      if (this.selectedAction) {
        if (this.selectedAction === 'activate') {
          this.toggleActivate(!this.input.is_active);
        } else {
          this.mode = 'delete';
        }
      } else {
        this.validateForm();
      }
    },

    validateEmail(rule, value, callback) {
      if (value === this.user.user.email) {
        callback(new Error(this.$t('common.rule.email.inputDifferent')));
      } else {
        callback();
      }
    },

    async validateForm() {
      try {
        await this.$refs.holderForm.validate();
        this.$confirm(this.$t('leader.coworker.confirmChanges'),
          '', {
            dangerouslyUseHTMLString: true,
            confirmButtonText: this.$t('common.save'),
            cancelButtonText: this.$t('common.goBack'),
            roundButton: true,
            customClass: 'stakeholderConfirmation',
          })
          .then(() => {
            this.editStakeholder();
          })
          .catch((err) => {
            if (err.msg) {
              this.$message.error(err.msg);
            }
            console.log(err);
          });
      } catch (err) {
        if (err.msg) {
          this.$message.error(err.msg);
        }
      }
    },

    async editStakeholder() {
      try {
        this.loading = true;
        const sendData = JSON.parse(JSON.stringify(this.input));
        if (this.role_type === 'coach') {
          await api
            .updateLeaderCoworker(this.$route.params.id, this.editDialog.user.uuid, sendData);
        } else {
          await api.updateStakeholder(this.editDialog.user.uuid, sendData);
        }
        this.$emit('closeSwipe');
        this.$emit('reload');
        this.resetForm();
        this.$message.success(this.$t('leader.coworker.infoSaved'));
        this.showLocal = false;
      } catch (err) {
        console.log(err);
        if (err.field) {
          this.apiRules = err.field;
          if (!this.isErrorMatchingField(this.input)) {
            this.$message.error(this.apiErrorMsg);
          }
        } else if (err.msg) {
          this.$message.error(err.msg);
        } else {
          this.$message.error(this.$t('leader.coworker.errorOccurred'));
        }
      } finally {
        this.loading = false;
      }
    },

    async toggleActivate(option) {
      try {
        this.loading = true;
        const sendData = { is_active: option };
        if (this.role_type === 'coach') {
          await api
            .updateLeaderCoworker(this.$route.params.id, this.editDialog.user.uuid, sendData);
        } else {
          await api.updateStakeholder(this.editDialog.user.uuid, sendData);
        }
        this.$emit('closeSwipe');
        this.$emit('reload');
        this.$message.success(this.$t('leader.coworker.infoSaved'));
        this.showLocal = false;
      } catch (err) {
        console.log(err);
        if (err.msg) {
          this.$message.error(err.msg);
        } else {
          this.$message.error(this.$t('leader.coworker.errorOccurred'));
        }
      } finally {
        this.loading = false;
      }
    },

    async deleteStakeholder() {
      try {
        this.loading = true;
        if (this.role_type === 'coach') {
          await api.deleteLeaderCoworker(this.$route.params.id, this.editDialog.user.uuid);
        } else {
          await api.deleteStakeholder(this.editDialog.user.uuid);
        }
        this.$emit('reload');
        this.$message.success(this.$t('leader.coworker.coworkerRemoved'));
        this.showLocal = false;
      } catch (err) {
        console.log(err);
        if (err.msg) {
          this.$message.error(err.msg);
        }
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
  #holderModal {
    .holderDialog {
      .el-dialog__body {
        .el-form {
          padding: 5px 25px 25px 5px;
          box-sizing: border-box;
          width: 100%;
        }

        .secondModeWrap {
          padding-right: 20px;

          .secondDesc {
            padding-bottom: 30px;
            word-break: break-word;
          }

          .secondValueRow {
            padding-bottom: 20px;

            .customLabel {
              font-weight: bold;
              font-size: 12px;
              padding-bottom: 10px;
            }

            .staticValue {
              word-break: break-word;
              font-size: 14px;
              padding-bottom: 10px;
            }
          }
        }
      }

      .el-dialog__footer {
        .buttonWrap {
          width: 100%;
          text-align: center;
          background: white;

          .el-col {
            .el-button {
              width: 90%;
            }
          }
        }
      }
    }
  }
</style>

<style lang="scss">
  #holderModal {
    overflow: auto;

    .holderDialog {
      margin-top: 5vh !important;

      .el-dialog__body {
        box-sizing: border-box;
        padding-right: 0;
        padding-bottom: 0;
        min-height: 100px;

        .el-form {
          .el-input {
            input {
              border-radius: 0;
              border-top-width: 0;
              border-left-width: 0;
              border-right-width: 0;
            }
          }

          .el-select {
            width: 100%;
          }

          .el-form-item {
            .el-input__inner {
              padding-left: 0;
            }

            &.is-error {
              .el-input__inner {
                border-bottom-color: #ffb8b8;
              }

              .el-form-item__error {
                color: #ffb8b8;
                font-size: 12px;
                line-height: 1.3em;
              }
            }
          }

          .actionDesc {
            display: flex;
            flex-wrap: wrap;
            line-height: 22px;
            text-align: left;
            font-weight: bold;
          }

          .actionWrap {
            display: flex;
            width: 100%;
            padding: 10px 10px 0;
            align-items: center;

            .nextActionRadio {
              width: 100%;
              display: flex;
              flex-wrap: wrap;

              .el-radio {
                width: 100%;
                padding: 5px 0;
                margin: 5px 15px 0 0;
                word-break: break-word;
                white-space: normal;
              }

              .additionalLabel {
                font-size: 11px;
                font-style: italic;
              }
            }
          }
        }
      }

      .el-dialog__footer {
        text-align: center;
      }
    }
  }

  .activateConfirmation {
    // Necessary scss property
    $tablet: 899px;

    .el-message-box__btns {
      text-align: center;

      .el-message-box__content {
        padding: 25px 15px;
      }

      button {
        width: 150px !important;

        @media (max-width: $tablet) {
          width: 100% !important;
        }
      }
    }
  }
</style>
